import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Award, Flame, Timer, Hash, TrendingUp, Dumbbell, BookOpen, CheckCircle, XCircle, ChevronDown, ChevronUp, RotateCcw, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import ActionControls from "@/components/ActionControls";
import AIRepCounter from "@/components/AIRepCounter";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BodyweightExercises = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("guide");
  const [selectedExercise, setSelectedExercise] = useState("pushups");
  const [reps, setReps] = useState("");
  const [duration, setDuration] = useState("");
  const [badges, setBadges] = useState({ badges: [], total_badges: 0, good_badges: 0, better_badges: 0, best_badges: 0, exercise_stats: {} });
  const [availableBadges, setAvailableBadges] = useState([]);
  const [history, setHistory] = useState([]);
  const [circuitData, setCircuitData] = useState({ pushup_reps: "", jumprope_seconds: "", squat_reps: "", rounds_completed: "" });
  const [loading, setLoading] = useState(false);
  const [repCounterOpen, setRepCounterOpen] = useState(false);
  const [hasAIAccess, setHasAIAccess] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
    fetchFeatureAccess();
  }, []);

  const fetchFeatureAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/subscription/feature-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasAIAccess(!!res.data.has_advanced_ai);
    } catch (e) {
      setHasAIAccess(false);
    }
  };

  // Map our internal exercise name → AIRepCounter exercise key
  const aiExerciseKey = (() => {
    if (selectedExercise === 'pushups') return 'pushup';
    if (selectedExercise === 'squats') return 'squat';
    if (selectedExercise === 'situps') return 'situp';
    return 'pushup';
  })();

  const aiSupportsCurrentExercise = ['pushups', 'squats', 'situps'].includes(selectedExercise);

  const handleAIRepClick = () => {
    if (!hasAIAccess) {
      toast.info("AI Rep Counter is a Pro/Elite feature. Redirecting…");
      setTimeout(() => navigate("/subscription"), 800);
      return;
    }
    setRepCounterOpen(true);
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [badgesRes, availableRes, historyRes] = await Promise.all([
        axios.get(`${API}/badges`, { headers }),
        axios.get(`${API}/badges/available`, { headers }),
        axios.get(`${API}/exercises/history`, { headers })
      ]);

      setBadges(badgesRes.data);
      setAvailableBadges(availableRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleResetExercises = async () => {
    if (!window.confirm('Reset all bodyweight exercise history and badges? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/exercises/reset`, { headers });
      setBadges({ badges: [], total_badges: 0, good_badges: 0, better_badges: 0, best_badges: 0, exercise_stats: {} });
      setHistory([]);
      setAvailableBadges([]);
      toast.success('Exercise history reset');
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const logExercise = async () => {
    if (!reps && !duration) {
      toast.error('Please enter reps or duration');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        exercise_type: selectedExercise,
        reps: reps ? parseInt(reps) : null,
        duration_seconds: duration ? parseInt(duration) : null
      };

      const response = await axios.post(`${API}/exercises/log`, payload, { headers });

      if (response.data.badge_earned) {
        toast.success(`${response.data.tier.toUpperCase()} tier achieved! Badge: ${response.data.badge_earned}`);
      } else {
        toast.success(`Exercise logged! Tier: ${response.data.tier || 'N/A'}`);
      }

      setReps("");
      setDuration("");
      fetchData();
    } catch (error) {
      toast.error('Failed to log exercise');
    } finally {
      setLoading(false);
    }
  };

  const logCircuit = async () => {
    if (!circuitData.pushup_reps || !circuitData.jumprope_seconds || !circuitData.squat_reps || !circuitData.rounds_completed) {
      toast.error('Please complete all circuit fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        pushup_reps: parseInt(circuitData.pushup_reps),
        jumprope_seconds: parseInt(circuitData.jumprope_seconds),
        squat_reps: parseInt(circuitData.squat_reps),
        rounds_completed: parseInt(circuitData.rounds_completed)
      };

      const response = await axios.post(`${API}/exercises/circuit`, payload, { headers });

      if (response.data.badges_earned && response.data.badges_earned.length > 0) {
        toast.success(`Circuit complete! Badges earned: ${response.data.badges_earned.join(', ')}`);
      } else {
        toast.success(`Circuit completed! ${response.data.rounds} rounds`);
      }

      setCircuitData({ pushup_reps: "", jumprope_seconds: "", squat_reps: "", rounds_completed: "" });
      fetchData();
    } catch (error) {
      toast.error('Failed to log circuit');
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      good: "text-green-500 bg-green-500/10 border-green-500/30",
      better: "text-blue-500 bg-blue-500/10 border-blue-500/30",
      best: "text-yellow-500 bg-yellow-500/10 border-yellow-500/30"
    };
    return colors[tier] || "text-gray-500 bg-gray-500/10 border-gray-500/30";
  };

  const getTierBadge = (tier) => {
    if (tier === "best") return <Trophy className="w-4 h-4" />;
    if (tier === "better") return <Award className="w-4 h-4" />;
    return <Flame className="w-4 h-4" />;
  };

  const [expandedExercise, setExpandedExercise] = useState(null);

  // Comprehensive exercise data with examples, images, and technique tips
  const EXERCISE_EXAMPLES = {
    pushups: {
      id: "pushups",
      name: "Push-ups",
      icon: "💪",
      unit: "reps",
      shortDescription: "Standard push-ups with proper form",
      fullDescription: "The push-up is a fundamental upper body exercise that targets your chest, shoulders, triceps, and core. It's essential for basketball players to develop pushing strength for contact situations.",
      stockImage: `${process.env.REACT_APP_BACKEND_URL}/api/manga/push_up_female.png`,
      illustrationImage: `${process.env.REACT_APP_BACKEND_URL}/api/manga/push_up.png`,
      musclesWorked: ["Chest (Pectorals)", "Shoulders (Deltoids)", "Triceps", "Core (Stabilizers)"],
      setupSteps: [
        "Start in a high plank position with hands shoulder-width apart",
        "Keep your body in a straight line from head to heels",
        "Engage your core and glutes throughout the movement",
        "Fingers should point forward or slightly outward"
      ],
      executionSteps: [
        "Lower your body by bending your elbows at a 45-degree angle",
        "Go down until your chest nearly touches the floor",
        "Keep your elbows tucked, not flaring out excessively",
        "Push through your palms to return to the starting position",
        "Fully extend arms at the top without locking elbows"
      ],
      doTips: [
        "Maintain a rigid, straight body throughout",
        "Control the descent (2 seconds down, 1 second up)",
        "Breathe in on the way down, exhale pushing up",
        "Keep your head neutral, looking slightly ahead"
      ],
      dontTips: [
        "Don't let your hips sag or pike up",
        "Don't flare elbows out at 90 degrees",
        "Don't rush through reps - quality over quantity",
        "Don't hold your breath"
      ],
      variations: [
        { name: "Knee Push-ups", difficulty: "Easier", description: "Perform on knees for reduced resistance" },
        { name: "Wide-Grip Push-ups", difficulty: "Standard", description: "Hands wider than shoulder-width for more chest focus" },
        { name: "Diamond Push-ups", difficulty: "Harder", description: "Hands together under chest for triceps emphasis" },
        { name: "Plyometric Push-ups", difficulty: "Advanced", description: "Explosive push to lift hands off ground" }
      ],
      basketballBenefit: "Develops pushing power for posting up, boxing out, and absorbing contact on drives to the basket."
    },
    jumprope: {
      id: "jumprope",
      name: "Jump Rope",
      icon: "🪢",
      unit: "seconds",
      shortDescription: "Continuous jump rope",
      fullDescription: "Jump rope is an exceptional conditioning tool that improves footwork, coordination, and cardiovascular endurance. It mimics the quick foot movements needed in basketball defense.",
      stockImage: `${process.env.REACT_APP_BACKEND_URL}/api/manga/jump_rope_female.png`,
      illustrationImage: `${process.env.REACT_APP_BACKEND_URL}/api/manga/jump_rope.png`,
      musclesWorked: ["Calves (Gastrocnemius)", "Quadriceps", "Core", "Shoulders", "Forearms"],
      setupSteps: [
        "Choose the right rope length (handles should reach your armpits)",
        "Stand on the center of the rope to check length",
        "Hold handles at hip height with elbows close to body",
        "Stand on a flat, even surface with slight give"
      ],
      executionSteps: [
        "Rotate the rope with your wrists, not your arms",
        "Jump only high enough to clear the rope (1-2 inches)",
        "Land softly on the balls of your feet",
        "Keep your jumps quick and rhythmic",
        "Maintain a steady breathing pattern"
      ],
      doTips: [
        "Keep elbows close to your sides",
        "Use wrist rotation rather than arm swings",
        "Stay on balls of feet, never flat-footed",
        "Start slow and build speed gradually"
      ],
      dontTips: [
        "Don't jump too high - wasted energy",
        "Don't tense up shoulders",
        "Don't look down at the rope",
        "Don't land on your heels"
      ],
      variations: [
        { name: "Basic Bounce", difficulty: "Beginner", description: "Two-foot jumps at steady pace" },
        { name: "Alternating Foot", difficulty: "Intermediate", description: "Running in place while jumping" },
        { name: "High Knees", difficulty: "Intermediate", description: "Drive knees up while jumping" },
        { name: "Double Unders", difficulty: "Advanced", description: "Rope passes twice per jump" }
      ],
      basketballBenefit: "Develops quick feet for defensive slides, improves conditioning for late-game situations, and enhances coordination for ball handling."
    },
    airsquats: {
      id: "airsquats",
      name: "Air Squats",
      icon: "🦵",
      unit: "reps",
      shortDescription: "Bodyweight squats to full depth",
      fullDescription: "The air squat is the foundation of lower body strength, crucial for jumping, acceleration, and maintaining a low defensive stance. It builds the powerful legs needed for basketball.",
      stockImage: `${process.env.REACT_APP_BACKEND_URL}/api/manga/squat_female.png`,
      illustrationImage: `${process.env.REACT_APP_BACKEND_URL}/api/manga/squat.png`,
      musclesWorked: ["Quadriceps", "Glutes", "Hamstrings", "Core", "Hip Flexors"],
      setupSteps: [
        "Stand with feet shoulder-width apart or slightly wider",
        "Point toes slightly outward (15-30 degrees)",
        "Arms can be at sides, in front, or crossed at chest",
        "Engage your core before beginning the descent"
      ],
      executionSteps: [
        "Initiate by pushing hips back (like sitting in a chair)",
        "Bend knees and lower until thighs are parallel or below",
        "Keep weight in heels and midfoot",
        "Knees should track over toes (not cave inward)",
        "Drive through heels to stand, squeezing glutes at top"
      ],
      doTips: [
        "Keep chest up and back flat throughout",
        "Push knees out in line with toes",
        "Go to full depth if mobility allows",
        "Control the descent, explode on the ascent"
      ],
      dontTips: [
        "Don't let knees cave inward",
        "Don't rise onto toes or let heels lift",
        "Don't round your lower back",
        "Don't cut depth short - full range builds full strength"
      ],
      variations: [
        { name: "Half Squats", difficulty: "Easier", description: "Descend only to 45 degrees" },
        { name: "Sumo Squats", difficulty: "Standard", description: "Wide stance, toes pointed out more" },
        { name: "Pause Squats", difficulty: "Harder", description: "Hold at bottom for 2-3 seconds" },
        { name: "Jump Squats", difficulty: "Advanced", description: "Explode into a jump from bottom position" }
      ],
      basketballBenefit: "Essential for vertical leap, first-step explosiveness, and maintaining defensive stance. Strong legs are the foundation of basketball performance."
    }
  };

  const exercises = Object.values(EXERCISE_EXAMPLES).map(ex => ({
    id: ex.id,
    name: ex.name,
    icon: ex.icon,
    unit: ex.unit,
    description: ex.shortDescription
  }));

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tight uppercase">
              BODYWEIGHT EXERCISES
            </h1>
            <p className="text-zinc-500 mt-1 text-sm">Track exercises, earn badges, build strength</p>
          </div>
          <ActionControls
            onReset={handleResetExercises}
            showUndo={false}
            showRedo={false}
            variant="compact"
            resetLabel="Reset exercise history"
          />
        </div>

        {/* Badge Summary - Clickable Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="badge-summary">
          <Card 
            className="bg-card border-border cursor-pointer hover:border-primary/50 transition-all active:scale-95"
            onClick={() => setActiveTab("badges")}
            data-testid="total-badges-card"
          >
            <CardContent className="p-4 text-center">
              <Trophy className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-3xl font-heading font-black text-white">{badges.total_badges}</p>
              <p className="text-xs text-muted-foreground">Total Badges</p>
            </CardContent>
          </Card>
          <Card 
            className="bg-green-500/10 border-green-500/30 cursor-pointer hover:border-green-500/60 transition-all active:scale-95"
            onClick={() => setActiveTab("badges")}
            data-testid="good-badges-card"
          >
            <CardContent className="p-4 text-center">
              <Flame className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-3xl font-heading font-black text-green-500">{badges.good_badges}</p>
              <p className="text-xs text-muted-foreground">Good Tier</p>
            </CardContent>
          </Card>
          <Card 
            className="bg-blue-500/10 border-blue-500/30 cursor-pointer hover:border-blue-500/60 transition-all active:scale-95"
            onClick={() => setActiveTab("badges")}
            data-testid="better-badges-card"
          >
            <CardContent className="p-4 text-center">
              <Award className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-3xl font-heading font-black text-blue-500">{badges.better_badges}</p>
              <p className="text-xs text-muted-foreground">Better Tier</p>
            </CardContent>
          </Card>
          <Card 
            className="bg-yellow-500/10 border-yellow-500/30 cursor-pointer hover:border-yellow-500/60 transition-all active:scale-95"
            onClick={() => setActiveTab("badges")}
            data-testid="best-badges-card"
          >
            <CardContent className="p-4 text-center">
              <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-3xl font-heading font-black text-yellow-500">{badges.best_badges}</p>
              <p className="text-xs text-muted-foreground">Best Tier</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-5 w-full h-auto p-1">
            <TabsTrigger value="guide" data-testid="tab-guide" className="text-xs md:text-sm py-2">Guide</TabsTrigger>
            <TabsTrigger value="exercises" data-testid="tab-exercises" className="text-xs md:text-sm py-2">Log</TabsTrigger>
            <TabsTrigger value="circuit" data-testid="tab-circuit" className="text-xs md:text-sm py-2">Circuit</TabsTrigger>
            <TabsTrigger value="badges" data-testid="tab-badges" className="text-xs md:text-sm py-2">Badges</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="text-xs md:text-sm py-2">History</TabsTrigger>
          </TabsList>

          {/* Exercise Guide with Examples */}
          <TabsContent value="guide" className="space-y-6">
            <div className="grid gap-6">
              {Object.values(EXERCISE_EXAMPLES).map((exercise) => (
                <Card key={exercise.id} className="bg-card border-border overflow-hidden" data-testid={`guide-${exercise.id}`}>
                  <CardHeader 
                    className="cursor-pointer hover:bg-background/50 transition-colors"
                    onClick={() => setExpandedExercise(expandedExercise === exercise.id ? null : exercise.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-4xl">{exercise.icon}</span>
                        <div>
                          <CardTitle className="text-xl font-heading font-bold text-white">{exercise.name}</CardTitle>
                          <CardDescription className="mt-1">{exercise.shortDescription}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-primary" />
                        {expandedExercise === exercise.id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  {expandedExercise === exercise.id && (
                    <CardContent className="space-y-6 border-t border-border pt-6">
                      {/* Images Section */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Reference Photo</p>
                          <div className="relative rounded-sm overflow-hidden aspect-video bg-background">
                            <img 
                              src={exercise.stockImage} 
                              alt={`${exercise.name} demonstration`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Form Illustration</p>
                          <div className="relative rounded-sm overflow-hidden aspect-video bg-background">
                            <img 
                              src={exercise.illustrationImage} 
                              alt={`${exercise.name} form guide`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Full Description */}
                      <div className="p-4 bg-primary/5 border border-primary/20 rounded-sm">
                        <p className="text-sm text-muted-foreground">{exercise.fullDescription}</p>
                      </div>

                      {/* Muscles Worked */}
                      <div>
                        <h4 className="text-sm font-bold text-white mb-3">Muscles Worked</h4>
                        <div className="flex flex-wrap gap-2">
                          {exercise.musclesWorked.map((muscle, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {muscle}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Setup Steps */}
                      <div>
                        <h4 className="text-sm font-bold text-white mb-3">Setup</h4>
                        <ol className="space-y-2">
                          {exercise.setupSteps.map((step, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                                {i + 1}
                              </span>
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>

                      {/* Execution Steps */}
                      <div>
                        <h4 className="text-sm font-bold text-white mb-3">Execution</h4>
                        <ol className="space-y-2">
                          {exercise.executionSteps.map((step, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/10 text-green-500 text-xs font-bold flex items-center justify-center">
                                {i + 1}
                              </span>
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>

                      {/* Do's and Don'ts */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <h4 className="text-sm font-bold text-green-500">DO</h4>
                          </div>
                          <ul className="space-y-2">
                            {exercise.doTips.map((tip, i) => (
                              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                <span className="text-green-500 mt-0.5">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <XCircle className="w-5 h-5 text-red-500" />
                            <h4 className="text-sm font-bold text-red-500">DON'T</h4>
                          </div>
                          <ul className="space-y-2">
                            {exercise.dontTips.map((tip, i) => (
                              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                <span className="text-red-500 mt-0.5">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Variations */}
                      <div>
                        <h4 className="text-sm font-bold text-white mb-3">Variations</h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {exercise.variations.map((variation, i) => (
                            <div key={i} className="p-3 bg-background rounded-sm border border-border">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-bold text-white">{variation.name}</p>
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    variation.difficulty === 'Easier' ? 'border-green-500/30 text-green-500' :
                                    variation.difficulty === 'Advanced' ? 'border-red-500/30 text-red-500' :
                                    variation.difficulty === 'Harder' ? 'border-yellow-500/30 text-yellow-500' :
                                    'border-blue-500/30 text-blue-500'
                                  }`}
                                >
                                  {variation.difficulty}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{variation.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Basketball Benefit */}
                      <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">🏀</span>
                          <div>
                            <h4 className="text-sm font-bold text-primary mb-1">Basketball Benefit</h4>
                            <p className="text-sm text-muted-foreground">{exercise.basketballBenefit}</p>
                          </div>
                        </div>
                      </div>

                      {/* Quick Log Button */}
                      <Button 
                        onClick={() => {
                          setSelectedExercise(exercise.id);
                          setActiveTab("exercises");
                        }}
                        className="w-full bg-primary hover:bg-primary/90 text-white font-bold"
                        data-testid={`quick-log-${exercise.id}`}
                      >
                        LOG {exercise.name.toUpperCase()}
                      </Button>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Individual Exercises */}
          <TabsContent value="exercises" className="space-y-6">
            <Card className="bg-card border-border" data-testid="exercise-tracker">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Log Exercise</CardTitle>
                <CardDescription>Track individual exercises and earn tier badges</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Exercise Selector */}
                <div className="grid grid-cols-3 gap-4">
                  {exercises.map((exercise) => (
                    <button
                      key={exercise.id}
                      onClick={() => setSelectedExercise(exercise.id)}
                      className={`p-4 rounded-sm border-2 transition-all ${
                        selectedExercise === exercise.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-background hover:border-primary/50'
                      }`}
                      data-testid={`select-${exercise.id}`}
                    >
                      <span className="text-4xl block mb-2">{exercise.icon}</span>
                      <p className="text-sm font-bold text-white">{exercise.name}</p>
                    </button>
                  ))}
                </div>

                {/* Input based on exercise type */}
                {selectedExercise && (
                  <div className="space-y-4">
                    <div className="p-4 bg-background rounded-sm border border-border">
                      <p className="text-sm text-muted-foreground mb-2">
                        {EXERCISE_EXAMPLES[selectedExercise]?.shortDescription}
                      </p>
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-primary text-xs"
                        onClick={() => {
                          setExpandedExercise(selectedExercise);
                          setActiveTab("guide");
                        }}
                      >
                        View full exercise guide →
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label>{selectedExercise === 'jumprope' ? 'Duration (seconds)' : 'Reps'}</Label>
                      {selectedExercise === 'jumprope' ? (
                        <div className="flex gap-2 items-center">
                          <Timer className="w-5 h-5 text-primary" />
                          <Input
                            type="number"
                            placeholder="Enter seconds"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            data-testid="duration-input"
                          />
                        </div>
                      ) : (
                        <div className="flex gap-2 items-center">
                          <Hash className="w-5 h-5 text-primary" />
                          <Input
                            type="number"
                            placeholder="Enter reps"
                            value={reps}
                            onChange={(e) => setReps(e.target.value)}
                            data-testid="reps-input"
                          />
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={logExercise}
                      disabled={loading}
                      className="w-full bg-primary hover:bg-primary/90 text-white font-bold uppercase"
                      data-testid="log-exercise-button"
                    >
                      {loading ? 'Logging...' : 'Log Exercise'}
                    </Button>

                    {/* AI Rep Counter (only for supported exercises) */}
                    {aiSupportsCurrentExercise && (
                      <>
                        <div className="relative my-3">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-zinc-800" />
                          </div>
                          <div className="relative flex justify-center">
                            <span className="bg-zinc-900 px-3 text-xs uppercase tracking-widest text-zinc-500">or</span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          onClick={handleAIRepClick}
                          className={
                            hasAIAccess
                              ? "w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-black uppercase tracking-wide"
                              : "w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-black uppercase tracking-wide border border-zinc-700 relative"
                          }
                          data-testid="ai-rep-counter-btn"
                        >
                          {hasAIAccess ? (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Count Reps with AI Camera
                            </>
                          ) : (
                            <>
                              <Lock className="w-4 h-4 mr-2 text-amber-500" />
                              <span>AI Rep Counter</span>
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded">
                                PRO
                              </span>
                            </>
                          )}
                        </Button>
                        <p className="text-[10px] text-zinc-600 text-center uppercase tracking-widest">
                          {hasAIAccess ? "Beta • Phone camera + AI" : "Upgrade to Pro or Elite to unlock"}
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Tier Requirements */}
                <div className="grid grid-cols-3 gap-3">
                  {['good', 'better', 'best'].map((tier) => {
                    const badge = availableBadges.find(b => b.exercise_type === selectedExercise && b.tier === tier);
                    return badge ? (
                      <div key={tier} className={`p-3 rounded-sm border ${getTierColor(tier)}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {getTierBadge(tier)}
                          <p className="text-xs font-bold uppercase">{tier}</p>
                        </div>
                        <p className="text-xs">{badge.requirement}</p>
                      </div>
                    ) : null;
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Exercise Stats */}
            {badges.exercise_stats && (
              <div className="grid md:grid-cols-3 gap-4">
                {exercises.map((exercise) => {
                  const stats = badges.exercise_stats[exercise.id];
                  return stats && stats.sessions > 0 ? (
                    <Card key={exercise.id} className="bg-card border-border" data-testid={`stats-${exercise.id}`}>
                      <CardHeader>
                        <CardTitle className="text-lg font-heading font-bold text-white flex items-center gap-2">
                          <span>{exercise.icon}</span>
                          {exercise.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Best:</span>
                            <span className="text-sm font-bold text-primary">{stats.best} {exercise.unit}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Average:</span>
                            <span className="text-sm font-bold text-white">{stats.avg} {exercise.unit}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Sessions:</span>
                            <span className="text-sm font-bold text-white">{stats.sessions}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : null;
                })}
              </div>
            )}
          </TabsContent>

          {/* Circuit Workout */}
          <TabsContent value="circuit" className="space-y-6">
            <Card className="bg-card border-border" data-testid="circuit-tracker">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Full Circuit Workout</CardTitle>
                <CardDescription>Complete all three exercises in rounds</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>💪 Push-ups (reps)</Label>
                    <Input
                      type="number"
                      placeholder="20"
                      value={circuitData.pushup_reps}
                      onChange={(e) => setCircuitData({...circuitData, pushup_reps: e.target.value})}
                      data-testid="circuit-pushups-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>🪢 Jump Rope (seconds)</Label>
                    <Input
                      type="number"
                      placeholder="60"
                      value={circuitData.jumprope_seconds}
                      onChange={(e) => setCircuitData({...circuitData, jumprope_seconds: e.target.value})}
                      data-testid="circuit-jumprope-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>🦵 Air Squats (reps)</Label>
                    <Input
                      type="number"
                      placeholder="30"
                      value={circuitData.squat_reps}
                      onChange={(e) => setCircuitData({...circuitData, squat_reps: e.target.value})}
                      data-testid="circuit-squats-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Rounds Completed</Label>
                  <Input
                    type="number"
                    placeholder="3"
                    value={circuitData.rounds_completed}
                    onChange={(e) => setCircuitData({...circuitData, rounds_completed: e.target.value})}
                    data-testid="circuit-rounds-input"
                  />
                </div>

                <Button
                  onClick={logCircuit}
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold uppercase"
                  data-testid="log-circuit-button"
                >
                  {loading ? 'Logging Circuit...' : 'Log Circuit Workout'}
                </Button>

                {/* Circuit Badge Requirements */}
                <div className="p-4 bg-background rounded-sm border border-border">
                  <p className="text-sm font-bold text-white mb-2">Circuit Badge Tiers:</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-xs">
                      <span className="text-green-500 font-bold">GOOD:</span> 1-2 rounds
                    </div>
                    <div className="text-xs">
                      <span className="text-blue-500 font-bold">BETTER:</span> 3-4 rounds
                    </div>
                    <div className="text-xs">
                      <span className="text-yellow-500 font-bold">BEST:</span> 5+ rounds
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Badges Gallery */}
          <TabsContent value="badges" className="space-y-6">
            <Card className="bg-card border-border" data-testid="badges-gallery">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Badge Collection</CardTitle>
                <CardDescription>Your earned achievements</CardDescription>
              </CardHeader>
              <CardContent>
                {badges.badges && badges.badges.length > 0 ? (
                  <div className="grid md:grid-cols-3 gap-4">
                    {badges.badges.map((badge) => (
                      <div
                        key={badge.id}
                        className={`p-4 rounded-sm border-2 ${getTierColor(badge.tier)}`}
                        data-testid={`badge-${badge.id}`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          {getTierBadge(badge.tier)}
                          <Badge className={getTierColor(badge.tier)}>
                            {badge.tier.toUpperCase()}
                          </Badge>
                        </div>
                        <p className="font-bold text-white mb-1">{badge.badge_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{badge.badge_type}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(badge.earned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No badges earned yet</p>
                    <p className="text-sm mt-2">Complete exercises to start earning badges!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Available Badges */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Available Badges</CardTitle>
                <CardDescription>All badges you can earn</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-3">
                  {availableBadges.map((badge, index) => {
                    const isEarned = badges.badges?.some(b => b.badge_name === badge.badge_name);
                    return (
                      <div
                        key={index}
                        className={`p-3 rounded-sm border ${isEarned ? getTierColor(badge.tier) : 'border-border bg-background/50 opacity-60'}`}
                        data-testid={`available-badge-${index}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {getTierBadge(badge.tier)}
                          <p className="text-xs font-bold uppercase">{badge.tier}</p>
                        </div>
                        <p className="text-xs font-bold text-white mb-1">{badge.badge_name}</p>
                        <p className="text-xs text-muted-foreground">{badge.requirement}</p>
                        {isEarned && (
                          <Badge className="mt-2 text-xs bg-green-500/10 text-green-500 border-green-500/30">
                            Earned ✓
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History */}
          <TabsContent value="history" className="space-y-6">
            <Card className="bg-card border-border" data-testid="exercise-history">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Exercise History</CardTitle>
              </CardHeader>
              <CardContent>
                {history.length > 0 ? (
                  <div className="space-y-2">
                    {history.map((exercise) => (
                      <div
                        key={exercise.id}
                        className="flex items-center justify-between p-3 bg-background rounded-sm border border-border"
                        data-testid={`history-${exercise.id}`}
                      >
                        <div>
                          <p className="font-medium text-white capitalize">{exercise.exercise_type}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(exercise.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm font-bold text-primary">
                              {exercise.reps ? `${exercise.reps} reps` : `${exercise.duration_seconds}s`}
                            </p>
                            {exercise.tier && (
                              <Badge className={`text-xs ${getTierColor(exercise.tier)}`}>
                                {exercise.tier}
                              </Badge>
                            )}
                          </div>
                          {exercise.badge_earned && getTierBadge(exercise.tier)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Dumbbell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No exercises logged yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <AIRepCounter
        open={repCounterOpen}
        onOpenChange={setRepCounterOpen}
        exerciseType={aiExerciseKey}
        onSaved={fetchData}
      />
    </div>
  );
};

export default BodyweightExercises;
